import React, { useRef, useState } from 'react';

// Module-level map to track ongoing thumbnail requests across re-renders
const ongoingThumbnailRequests = new Map<string, Promise<string | null>>();
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  LinearProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import {
  JobDocument,
  DocumentType,
  DocumentCategory,
  documentService,
  formatFileSize,
  getCategoryDisplayName,
  getFileIcon
} from '../../services/documentService';

interface DocumentGalleryProps {
  jobId: string;
  uploadedDocuments: JobDocument[];
  loadingDocuments: boolean;
  isOffline: boolean;
  uploadConstraints: any;
  onDocumentClick: (document: JobDocument) => void;
  onDocumentGalleryClick: (document: JobDocument, section: 'uploaded' | 'other') => void;
  onDownloadDocument: (document: JobDocument, event: React.MouseEvent) => void;
  onDeleteDocument: (document: JobDocument, event: React.MouseEvent) => void;
  onReloadDocuments: (forceRefresh?: boolean) => void;
  thumbnailUrls: { [key: string]: string | null };
  onThumbnailLoaded: (documentId: string, url: string | null) => void;
}

const DocumentGallery: React.FC<DocumentGalleryProps> = ({
  jobId,
  uploadedDocuments,
  loadingDocuments,
  isOffline,
  uploadConstraints,
  onDocumentClick,
  onDocumentGalleryClick,
  onDownloadDocument,
  onDeleteDocument,
  onReloadDocuments,
  thumbnailUrls,
  onThumbnailLoaded
}) => {
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory>(DocumentCategory.Other);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documentCategoryFilter, setDocumentCategoryFilter] = useState<number | 'all'>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Eagerly load thumbnails/full images for all visible image documents
  React.useEffect(() => {
    const imageDocsWithThumbnail = uploadedDocuments.filter(doc => {
      const ext = doc.fileExtension.toLowerCase().replace('.', '');
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext) && doc.hasThumbnail;
    });
    
    const imageDocsWithoutThumbnail = uploadedDocuments.filter(doc => {
      const ext = doc.fileExtension.toLowerCase().replace('.', '');
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext) && !doc.hasThumbnail;
    });
    
    console.log(`[DocumentGallery] Found ${imageDocsWithThumbnail.length} images with thumbnails and ${imageDocsWithoutThumbnail.length} images without thumbnails`);
    
    // Load thumbnails for images that have them
    imageDocsWithThumbnail.forEach(doc => {
      if (thumbnailUrls[doc.id] === undefined && !ongoingThumbnailRequests.has(doc.id)) {
        console.log(`[DocumentGallery] Loading thumbnail for ${doc.originalFileName} (${doc.id})`);
        loadThumbnail(doc);
      }
    });
    
    // Load full images for images without thumbnails
    imageDocsWithoutThumbnail.forEach(doc => {
      if (thumbnailUrls[doc.id] === undefined && !ongoingThumbnailRequests.has(doc.id)) {
        console.log(`[DocumentGallery] Loading full image for ${doc.originalFileName} (${doc.id}) - no thumbnail available`);
        loadFullImage(doc);
      }
    });
  }, [uploadedDocuments, thumbnailUrls]);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !jobId) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);

      let uploadedCount = 0;
      const totalFiles = files.length;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file
        if (uploadConstraints) {
          const validation = await documentService.validateFile(file, uploadConstraints);
          if (!validation.isValid) {
            alert(`File "${file.name}": ${validation.error}`);
            return;
          }
        }

        try {
          await documentService.uploadDocument(file, {
            jobId,
            category: uploadCategory,
            description: undefined
          });

          uploadedCount++;
          setUploadProgress((uploadedCount / totalFiles) * 100);
        } catch (fileErr: any) {
          console.error(`Failed to upload ${file.name}:`, fileErr);
          alert(`Failed to upload "${file.name}": ${fileErr.response?.data?.error || fileErr.message}`);
          return;
        }
      }

      // Documents uploaded successfully - gallery will refresh automatically
      setUploadCategory(DocumentCategory.Other);
      onReloadDocuments(true);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Refresh documents list
      onReloadDocuments();
    } catch (err: any) {
      console.error('Failed to upload documents:', err);
      alert(err.response?.data?.error || err.message || 'Failed to upload documents');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getDocumentIcon = (document: JobDocument) => {
    const iconType = getFileIcon(document.fileExtension);
    switch (iconType) {
      case 'pdf':
        return <PdfIcon sx={{ fontSize: 48, color: 'error.main' }} />;
      case 'image':
        return <ImageIcon sx={{ fontSize: 48, color: 'info.main' }} />;
      case 'word':
        return <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />;
      case 'excel':
        return <DescriptionIcon sx={{ fontSize: 48, color: 'success.main' }} />;
      default:
        return <FileIcon sx={{ fontSize: 48, color: 'text.secondary' }} />;
    }
  };

  // Load thumbnail for a document using shared state
  const loadThumbnail = async (document: JobDocument) => {
    if (!document.hasThumbnail) {
      console.log(`[DocumentGallery] Document ${document.id} has no thumbnail flag`);
      return null;
    }

    // Check if request is already ongoing
    const existingRequest = ongoingThumbnailRequests.get(document.id);
    if (existingRequest) {
      console.log(`[DocumentGallery] Thumbnail request already ongoing for ${document.id}`);
      // Return the existing promise to avoid duplicate requests
      return existingRequest;
    }

    // Create and store the request promise
    const requestPromise = (async () => {
      try {
        console.log(`[DocumentGallery] Requesting thumbnail from API for ${document.id}`);
        const dataUrl = await documentService.getThumbnailDataUrl(document.id);
        console.log(`[DocumentGallery] Thumbnail loaded successfully for ${document.id}`);
        onThumbnailLoaded(document.id, dataUrl);
        return dataUrl;
      } catch (error) {
        console.warn(`[DocumentGallery] Failed to load thumbnail for document ${document.id}:`, error);
        onThumbnailLoaded(document.id, null); // Mark as failed
        return null;
      } finally {
        // Remove from ongoing requests map
        ongoingThumbnailRequests.delete(document.id);
      }
    })();

    // Store the promise to prevent duplicate requests
    ongoingThumbnailRequests.set(document.id, requestPromise);

    return requestPromise;
  };

  // Load full image for documents without thumbnails
  const loadFullImage = async (document: JobDocument) => {
    // Check if request is already ongoing
    const existingRequest = ongoingThumbnailRequests.get(document.id);
    if (existingRequest) {
      console.log(`[DocumentGallery] Full image request already ongoing for ${document.id}`);
      return existingRequest;
    }

    // Create and store the request promise
    const requestPromise = (async () => {
      try {
        console.log(`[DocumentGallery] Requesting full image from API for ${document.id}`);
        const blob = await documentService.downloadDocument(document.id);
        const dataUrl = await new Promise<string | null>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
        console.log(`[DocumentGallery] Full image loaded successfully for ${document.id}`);
        onThumbnailLoaded(document.id, dataUrl);
        return dataUrl;
      } catch (error) {
        console.warn(`[DocumentGallery] Failed to load full image for document ${document.id}:`, error);
        onThumbnailLoaded(document.id, null); // Mark as failed
        return null;
      } finally {
        // Remove from ongoing requests map
        ongoingThumbnailRequests.delete(document.id);
      }
    })();

    // Store the promise to prevent duplicate requests
    ongoingThumbnailRequests.set(document.id, requestPromise);

    return requestPromise;
  };


  const getDocumentDisplay = (document: JobDocument) => {
    // For images, show thumbnail if available, otherwise show icon
    const ext = document.fileExtension.toLowerCase().replace('.', '');
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);

    if (isImage && document.hasThumbnail) {
      const thumbnailUrl = thumbnailUrls[document.id];

      if (thumbnailUrl) {
        // Thumbnail is loaded
        return (
          <Box
            component="img"
            src={thumbnailUrl}
            alt={document.originalFileName}
            sx={{
              width: '100px',
              height: '100px',
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}
            onError={() => {
              // If thumbnail fails to load, mark as failed
              onThumbnailLoaded(document.id, null);
            }}
          />
        );
      } else if (thumbnailUrl === undefined) {
        // Thumbnail not yet loaded - start loading
        loadThumbnail(document);
        // Show loading state while loading
        return (
          <Box
            sx={{
              width: '100px',
              height: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.100'
            }}
          >
            <CircularProgress size={20} />
          </Box>
        );
      } else {
        // Thumbnail failed to load, show icon
        return getDocumentIcon(document);
      }
    } else if (isImage && !document.hasThumbnail) {
      // Image without thumbnail - load full image instead
      const fullImageUrl = thumbnailUrls[document.id];

      if (fullImageUrl) {
        // Full image is loaded
        return (
          <Box
            component="img"
            src={fullImageUrl}
            alt={document.originalFileName}
            sx={{
              width: '100px',
              height: '100px',
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}
            onError={() => {
              // If full image fails to load, mark as failed
              onThumbnailLoaded(document.id, null);
            }}
          />
        );
      } else if (fullImageUrl === undefined) {
        // Full image not yet loaded - start loading
        loadFullImage(document);
        // Show loading state while loading
        return (
          <Box
            sx={{
              width: '100px',
              height: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.100'
            }}
          >
            <CircularProgress size={20} />
          </Box>
        );
      } else {
        // Full image failed to load, show icon
        return getDocumentIcon(document);
      }
    }

    // For non-images, show icon
    return getDocumentIcon(document);
  };

  const filteredDocuments = uploadedDocuments.filter(d => {
    const isUploaded = d.documentType === DocumentType.Uploaded || d.documentType === 'Uploaded' || d.documentType === 1;


    if (documentCategoryFilter === 'all') {
      const result = isUploaded;
      return result;
    }

    const result = isUploaded && d.category === documentCategoryFilter;
    return result;
  });


  return (
    <Card>
      <CardContent>
        {/* Upload Section */}
        {!isOffline && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Quick Upload</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={uploadCategory}
                  label="Category"
                  onChange={(e) => setUploadCategory(e.target.value as DocumentCategory)}
                  disabled={isUploading}
                >
                  <MenuItem value={DocumentCategory.InitialPhotos}>Initial Photos</MenuItem>
                  <MenuItem value={DocumentCategory.DesignRenders}>Design Renders</MenuItem>
                  <MenuItem value={DocumentCategory.Contracts}>Contracts</MenuItem>
                  <MenuItem value={DocumentCategory.Quotes}>Quotes</MenuItem>
                  <MenuItem value={DocumentCategory.Measurements}>Measurements</MenuItem>
                  <MenuItem value={DocumentCategory.InstallationPhotos}>Installation Photos</MenuItem>
                  <MenuItem value={DocumentCategory.Correspondence}>Correspondence</MenuItem>
                  <MenuItem value={DocumentCategory.TechnicalSpecs}>Technical Specs</MenuItem>
                  <MenuItem value={DocumentCategory.Other}>Other</MenuItem>
                </Select>
              </FormControl>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                style={{ display: 'none' }}
                accept={uploadConstraints?.allowedExtensions.join(',') || '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.bmp,.webp'}
              />
              <Button
                variant="contained"
                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                onClick={handleFileSelect}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Select & Upload Files'}
              </Button>
            </Box>
            {isUploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  color="primary"
                />
              </Box>
            )}
            {uploadConstraints && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Max file size: {uploadConstraints.maxFileSizeMb.toFixed(1)} MB per file.
                Total job limit: {uploadConstraints.maxTotalJobSizeMb.toFixed(1)} MB.
                Allowed types: {uploadConstraints.allowedExtensions.join(', ')}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {loadingDocuments ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Uploaded Documents Section */}
            {uploadedDocuments.filter(d => d.documentType === DocumentType.Uploaded || d.documentType === 'Uploaded' || d.documentType === 1).length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Uploaded Documents</Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter by Category</InputLabel>
                    <Select
                      value={documentCategoryFilter.toString()}
                      label="Filter by Category"
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setDocumentCategoryFilter(newValue === 'all' ? 'all' : parseInt(newValue, 10));
                      }}
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      <MenuItem value={1}>{getCategoryDisplayName(DocumentCategory.InitialPhotos)}</MenuItem>
                      <MenuItem value={2}>{getCategoryDisplayName(DocumentCategory.DesignRenders)}</MenuItem>
                      <MenuItem value={3}>{getCategoryDisplayName(DocumentCategory.Contracts)}</MenuItem>
                      <MenuItem value={4}>{getCategoryDisplayName(DocumentCategory.Quotes)}</MenuItem>
                      <MenuItem value={5}>{getCategoryDisplayName(DocumentCategory.Measurements)}</MenuItem>
                      <MenuItem value={6}>{getCategoryDisplayName(DocumentCategory.InstallationPhotos)}</MenuItem>
                      <MenuItem value={7}>{getCategoryDisplayName(DocumentCategory.Correspondence)}</MenuItem>
                      <MenuItem value={8}>{getCategoryDisplayName(DocumentCategory.TechnicalSpecs)}</MenuItem>
                      <MenuItem value={10}>{getCategoryDisplayName(DocumentCategory.Other)}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {filteredDocuments.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      minHeight: '240px',
                      overflowY: 'auto',
                      p: 1
                    }}
                  >
                    {filteredDocuments.map((document) => (
                      <Box key={document.id}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 2,
                          mb: 4,
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            '& .document-actions': {
                              opacity: 1
                            }
                          },
                          height: '160px',
                          minWidth: '140px',
                          maxWidth: '140px',
                          flex: '0 0 auto',
                          position: 'relative',
                          '&:hover .preview-indicator': {
                            opacity: 1
                          }
                        }}
                        onClick={() => {
                          const ext = document.fileExtension.toLowerCase().replace('.', '');
                          if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext)) {
                            // Images and PDFs open in lightbox - specify 'uploaded' section
                            onDocumentGalleryClick(document, 'uploaded');
                          } else {
                            // Other documents open in new tab for preview
                            onDocumentClick(document);
                          }
                        }}
                      >
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                          {getDocumentDisplay(document)}
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: 'center',
                              wordBreak: 'break-word',
                              fontSize: '0.75rem',
                              lineHeight: 1.2,
                              maxWidth: '100%',
                              mt: 1,
                              flexShrink: 1,
                              minHeight: '2.4em',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {document.originalFileName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem', flexShrink: 0 }}
                          >
                            {formatFileSize(document.fileSizeBytes)}
                          </Typography>
                        </Box>

                        {/* Preview indicator for non-image, non-PDF documents */}
                        {(() => {
                          const ext = document.fileExtension.toLowerCase().replace('.', '');
                          const isPreviewable = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext);
                          if (!isPreviewable) {
                            return (
                              <Box
                                className="preview-indicator"
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.7rem',
                                  opacity: 0,
                                  transition: 'opacity 0.2s',
                                  pointerEvents: 'none'
                                }}
                              >
                                Click to preview
                              </Box>
                            );
                          }
                          return null;
                        })()}

                        <Box
                          className="document-actions"
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            mt: 'auto',
                            pt: 1
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={(e) => onDownloadDocument(document, e)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!isOffline && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => onDeleteDocument(document, e)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No documents match the selected category filter.
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {/* Other Documents Section - catch any documents that don't fit above categories */}
            {uploadedDocuments.filter(d =>
              d.documentType !== DocumentType.Uploaded && d.documentType !== 'Uploaded' && d.documentType !== 1
            ).length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>Other Documents</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    minHeight: '120px',
                    overflowY: 'auto',
                    p: 1
                  }}
                >
                  {uploadedDocuments
                    .filter(d =>
                      d.documentType !== DocumentType.Uploaded
                    )
                    .map((document) => (
                      <Box key={document.id}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 2,
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            '& .document-actions': {
                              opacity: 1
                            }
                          },
                          height: '160px',
                          minWidth: '120px',
                          flex: '0 0 auto',
                          position: 'relative'
                        }}
                        onClick={() => {
                          const ext = document.fileExtension.toLowerCase().replace('.', '');
                          if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext)) {
                            // Images and PDFs open in lightbox - specify 'other' section
                            onDocumentGalleryClick(document, 'other');
                          } else {
                            // Other documents open in new tab for preview
                            onDocumentClick(document);
                          }
                        }}
                      >
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                          {getDocumentDisplay(document)}
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: 'center',
                              wordBreak: 'break-word',
                              fontSize: '0.75rem',
                              lineHeight: 1.2,
                              maxWidth: '100%',
                              mt: 1,
                              flexShrink: 1,
                              minHeight: '2.4em',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {document.originalFileName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem', flexShrink: 0 }}
                          >
                            {formatFileSize(document.fileSizeBytes)}
                          </Typography>
                        </Box>

                        {/* Preview indicator for non-image, non-PDF documents */}
                        {(() => {
                          const ext = document.fileExtension.toLowerCase().replace('.', '');
                          const isPreviewable = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext);
                          if (!isPreviewable) {
                            return (
                              <Box
                                className="preview-indicator"
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.7rem',
                                  opacity: 0,
                                  transition: 'opacity 0.2s',
                                  pointerEvents: 'none'
                                }}
                              >
                                Click to preview
                              </Box>
                            );
                          }
                          return null;
                        })()}

                        <Box
                          className="document-actions"
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            mt: 'auto',
                            pt: 1
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={(e) => onDownloadDocument(document, e)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!isOffline && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => onDeleteDocument(document, e)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    ))}
                </Box>
              </>
            )}

          </>
        )}
      </CardContent>
    </Card>
  );
};



export default DocumentGallery;
